import type { RbacV2Resource } from './rbac.v2.js'

/**
 * Ressources de champs de la fiche collaborateur : chaque clé de `HR_FIELD_REGISTRY` appartient à
 * exactement une d'entre elles. `read` sur la ressource donne le groupe dans la réponse de détail,
 * `edit` autorise la modification de ses champs.
 */
export const HR_FIELD_RESOURCES = [
  'hr.staff.identity',
  'hr.staff.position',
  'hr.staff.assignments',
  'hr.staff.contract',
  'hr.staff.contract-terms',
  'hr.staff.cost',
  'hr.staff.offer',
  'hr.staff.comment',
] as const satisfies readonly RbacV2Resource[]

export type HrFieldResource = (typeof HR_FIELD_RESOURCES)[number]

export interface HrFieldDescriptor {
  /** Ressource RBAC v2 qui porte ce champ (une et une seule). */
  resource: HrFieldResource
  /** Chemins effectivement observés dans les réponses fiche/contrat. */
  readPaths: readonly string[]
  /** Clés wire acceptées par les écritures fiche/contrat. */
  writePaths: readonly string[]
  /** Donnée calculée par le serveur, donc toujours en lecture seule. */
  computed: boolean
}

/**
 * Registre fermé des champs protégeables d'une fiche collaborateur et de ses contrats.
 *
 * Les disponibilités, formations, absences, équipements et vêtements sont volontairement absents :
 * ils possèdent chacun une ressource RBAC dédiée. Les chemins sont descriptifs et ne doivent jamais
 * être interprétés comme des expressions exécutables.
 */
export const HR_FIELD_REGISTRY = {
  firstName: {
    resource: 'hr.staff.identity',
    readPaths: ['admin.firstName'],
    writePaths: ['firstName'],
    computed: false,
  },
  lastName: {
    resource: 'hr.staff.identity',
    readPaths: ['admin.lastName'],
    writePaths: ['lastName'],
    computed: false,
  },
  email: {
    resource: 'hr.staff.identity',
    readPaths: ['admin.email'],
    writePaths: ['email'],
    computed: false,
  },
  phone: {
    resource: 'hr.staff.identity',
    readPaths: ['phone'],
    writePaths: ['phone'],
    computed: false,
  },
  role: {
    resource: 'hr.staff.position',
    readPaths: ['role'],
    writePaths: ['role'],
    computed: false,
  },
  contractType: {
    // Le type de contrat suit les conditions de contrat (durée, agence) : la matrice l'ouvre aux
    // opérationnels pour l'intérim, alors que matricule et dates restent réservés aux RH. Le passage
    // vers CDI/CDD reste réservé aux RH par la population (avant et après modification).
    resource: 'hr.staff.contract-terms',
    readPaths: ['contractType', 'activeContract.contractType', 'contractPeriods[].contractType'],
    writePaths: ['contractType'],
    computed: false,
  },
  status: {
    resource: 'hr.staff.position',
    readPaths: ['status'],
    writePaths: ['status'],
    computed: false,
  },
  gender: {
    resource: 'hr.staff.identity',
    readPaths: ['gender'],
    writePaths: ['gender'],
    computed: false,
  },
  birthDate: {
    resource: 'hr.staff.identity',
    readPaths: ['birthDate'],
    writePaths: ['birthDate'],
    computed: false,
  },
  profilePicture: {
    resource: 'hr.staff.identity',
    readPaths: ['profilePictureUrl'],
    writePaths: ['profilePicture', 'removeProfilePicture'],
    computed: false,
  },
  storeIds: {
    resource: 'hr.staff.assignments',
    readPaths: ['stores[].id', 'stores[].label'],
    writePaths: ['storeIds'],
    computed: false,
  },
  mainStoreId: {
    resource: 'hr.staff.assignments',
    readPaths: ['mainStoreId'],
    writePaths: ['mainStoreId'],
    computed: false,
  },
  matricule: {
    resource: 'hr.staff.contract',
    readPaths: ['matricule'],
    writePaths: ['matricule'],
    computed: false,
  },
  interimMatricule: {
    resource: 'hr.staff.contract',
    readPaths: ['interimMatricule'],
    writePaths: [],
    computed: false,
  },
  managerId: {
    resource: 'hr.staff.position',
    readPaths: ['managerId'],
    writePaths: ['managerId'],
    computed: false,
  },
  weeklyWorkingMinutes: {
    resource: 'hr.staff.contract-terms',
    readPaths: [
      'weeklyWorkingMinutes',
      'activeContract.weeklyWorkingMinutes',
      'contractPeriods[].weeklyWorkingMinutes',
    ],
    writePaths: ['weeklyWorkingMinutes'],
    computed: false,
  },
  hourlyCost: {
    resource: 'hr.staff.cost',
    readPaths: ['hourlyCost'],
    writePaths: ['hourlyCost'],
    computed: false,
  },
  professionalStatus: {
    resource: 'hr.staff.position',
    readPaths: ['professionalStatus'],
    writePaths: ['professionalStatus'],
    computed: false,
  },
  entryDate: {
    resource: 'hr.staff.contract',
    readPaths: ['entryDate'],
    writePaths: ['entryDate'],
    computed: false,
  },
  seniorityDate: {
    resource: 'hr.staff.contract',
    readPaths: ['seniorityDate'],
    writePaths: ['seniorityDate'],
    computed: false,
  },
  trialPeriodEndDate: {
    resource: 'hr.staff.contract',
    readPaths: ['trialPeriodEndDate'],
    writePaths: ['trialPeriodEndDate'],
    computed: false,
  },
  startDate: {
    resource: 'hr.staff.contract',
    readPaths: ['contractStartDate', 'activeContract.startDate', 'contractPeriods[].startDate'],
    writePaths: ['startDate'],
    computed: false,
  },
  endDate: {
    resource: 'hr.staff.contract',
    readPaths: ['contractEndDate', 'activeContract.endDate', 'contractPeriods[].endDate'],
    writePaths: ['endDate', 'removeEndDate'],
    computed: false,
  },
  interimAgencyId: {
    resource: 'hr.staff.contract-terms',
    readPaths: [
      'interimAgencyId',
      'activeContract.interimAgencyId',
      'contractPeriods[].interimAgencyId',
    ],
    writePaths: ['interimAgencyId'],
    computed: false,
  },
  offerAccessEnabled: {
    resource: 'hr.staff.offer',
    readPaths: ['offerAccessEnabled'],
    writePaths: ['offerAccessEnabled'],
    computed: false,
  },
  offerAccessOverride: {
    resource: 'hr.staff.offer',
    readPaths: ['offerAccessOverride'],
    writePaths: ['offerAccessOverride'],
    computed: false,
  },
  statusOverride: {
    resource: 'hr.staff.comment',
    readPaths: ['statusOverride'],
    writePaths: [],
    computed: false,
  },
  comments: {
    resource: 'hr.staff.comment',
    readPaths: ['comments'],
    writePaths: ['comments'],
    computed: false,
  },
  titularisationDate: {
    resource: 'hr.staff.contract',
    readPaths: [
      'titularisationDate',
      'activeContract.titularisationDate',
      'contractPeriods[].titularisationDate',
    ],
    writePaths: [],
    computed: true,
  },
  cumulativeInterimDays: {
    resource: 'hr.staff.contract',
    readPaths: [
      'cumulativeInterimDays',
      'activeContract.cumulativeInterimDays',
      'contractPeriods[].cumulativeInterimDays',
    ],
    writePaths: [],
    computed: true,
  },
  finDeCarenceDate: {
    resource: 'hr.staff.contract',
    readPaths: [
      'finDeCarenceDate',
      'activeContract.finDeCarenceDate',
      'contractPeriods[].finDeCarenceDate',
    ],
    writePaths: [],
    computed: true,
  },
  maxMaintienInterimDate: {
    resource: 'hr.staff.contract',
    readPaths: [
      'maxMaintienInterimDate',
      'activeContract.maxMaintienInterimDate',
      'contractPeriods[].maxMaintienInterimDate',
    ],
    writePaths: [],
    computed: true,
  },
  contractStartedAt: {
    resource: 'hr.staff.contract',
    readPaths: ['activeContract.startedAt', 'contractPeriods[].startedAt'],
    writePaths: [],
    computed: false,
  },
  contractEndedAt: {
    resource: 'hr.staff.contract',
    readPaths: ['activeContract.endedAt', 'contractPeriods[].endedAt'],
    writePaths: [],
    computed: false,
  },
  contractStartedById: {
    resource: 'hr.staff.contract',
    readPaths: ['activeContract.startedById', 'contractPeriods[].startedById'],
    writePaths: [],
    computed: false,
  },
  contractEndedById: {
    resource: 'hr.staff.contract',
    readPaths: ['activeContract.endedById', 'contractPeriods[].endedById'],
    writePaths: [],
    computed: false,
  },
} as const satisfies Record<string, HrFieldDescriptor>

export type HrField = keyof typeof HR_FIELD_REGISTRY

/** Ressource RBAC v2 qui porte un champ de la fiche collaborateur. */
export const resourceOfHrField = (field: HrField): HrFieldResource =>
  HR_FIELD_REGISTRY[field].resource

/** Champs du registre portés par une ressource de champs, dans l'ordre de déclaration. */
export const hrFieldsOfResource = (resource: HrFieldResource): HrField[] =>
  (Object.keys(HR_FIELD_REGISTRY) as HrField[]).filter(
    (field) => HR_FIELD_REGISTRY[field].resource === resource,
  )
